import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  Send, 
  Users, 
  MessageSquare, 
  AlertTriangle,
  CheckCircle,
  Info,
  X,
  Plus
} from 'lucide-react';
import { api } from '../services/api';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface NotificationStats {
  totalCount: number;
  readCount: number;
  unreadCount: number;
  readRate: number;
  byType: Array<{
    type: string;
    count: number;
  }>;
}

const NotificationsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'create' | 'stats'>('create');
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info' as 'info' | 'warning' | 'success' | 'error' | 'announcement',
    isGlobal: true,
    selectedUsers: [] as string[]
  });

  useEffect(() => {
    loadUsers();
    loadStats();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get('/notifications/users');
      if (response.data.success) {
        setUsers(response.data.data);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadStats = async () => {
    try {
      // TODO: Implement /notifications/stats endpoint
      // const response = await api.get('/notifications/stats');
      // if (response.data.success) {
      //   setStats(response.data.data);
      // }
      
      // Temporary mock data
      setStats({
        totalCount: 156,
        readCount: 133,
        unreadCount: 23,
        readRate: 85.3,
        byType: [
          { type: 'email', count: 89 },
          { type: 'system', count: 67 }
        ]
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.message.trim()) {
      setError('Título y mensaje son requeridos');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/notifications/create', {
        title: formData.title,
        message: formData.message,
        type: formData.type,
        isGlobal: formData.isGlobal,
        userIds: formData.isGlobal ? undefined : formData.selectedUsers
      });

      if (response.data.success) {
        setSuccess(response.data.message);
        setFormData({
          title: '',
          message: '',
          type: 'info',
          isGlobal: true,
          selectedUsers: []
        });
        loadStats(); // Refresh stats
      } else {
        setError(response.data.message);
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Error al enviar notificación');
    } finally {
      setLoading(false);
    }
  };

  const handleUserToggle = (userId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedUsers: prev.selectedUsers.includes(userId)
        ? prev.selectedUsers.filter(id => id !== userId)
        : [...prev.selectedUsers, userId]
    }));
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'warning': return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'success': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error': return <X className="w-4 h-4 text-red-500" />;
      case 'announcement': return <MessageSquare className="w-4 h-4 text-purple-500" />;
      default: return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'warning': return 'bg-yellow-100 border-yellow-300';
      case 'success': return 'bg-green-100 border-green-300';
      case 'error': return 'bg-red-100 border-red-300';
      case 'announcement': return 'bg-purple-100 border-purple-300';
      default: return 'bg-blue-100 border-blue-300';
    }
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Sistema de Notificaciones
        </h1>
        <p className="text-gray-600">
          Envía notificaciones a los estudiantes y gestiona las comunicaciones
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('create')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'create'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Plus className="w-4 h-4 inline mr-2" />
              Crear Notificación
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'stats'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Bell className="w-4 h-4 inline mr-2" />
              Estadísticas
            </button>
          </nav>
        </div>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Create Notification Tab */}
      {activeTab === 'create' && (
        <div className="bg-white rounded-lg shadow p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Notification Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Notificación
              </label>
              <div className="grid grid-cols-5 gap-3">
                {[
                  { value: 'info', label: 'Información', icon: Info },
                  { value: 'success', label: 'Éxito', icon: CheckCircle },
                  { value: 'warning', label: 'Advertencia', icon: AlertTriangle },
                  { value: 'error', label: 'Error', icon: X },
                  { value: 'announcement', label: 'Anuncio', icon: MessageSquare }
                ].map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, type: value as any }))}
                    className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                      formData.type === value 
                        ? getTypeColor(value) 
                        : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-xs font-medium">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Título
              </label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Título de la notificación"
                maxLength={100}
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.title.length}/100 caracteres
              </p>
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                Mensaje
              </label>
              <textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Contenido de la notificación..."
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.message.length}/500 caracteres
              </p>
            </div>

            {/* Target Audience */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Destinatarios
              </label>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="audience"
                    checked={formData.isGlobal}
                    onChange={() => setFormData(prev => ({ ...prev, isGlobal: true, selectedUsers: [] }))}
                    className="mr-2"
                  />
                  <Users className="w-4 h-4 mr-2" />
                  <span>Todos los estudiantes ({users.length} usuarios)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="audience"
                    checked={!formData.isGlobal}
                    onChange={() => setFormData(prev => ({ ...prev, isGlobal: false }))}
                    className="mr-2"
                  />
                  <span>Estudiantes específicos</span>
                </label>
              </div>

              {/* User Selection */}
              {!formData.isGlobal && (
                <div className="mt-4 max-h-60 overflow-y-auto border border-gray-200 rounded-md">
                  <div className="p-3">
                    <p className="text-sm text-gray-600 mb-2">
                      Selecciona estudiantes ({formData.selectedUsers.length} seleccionados)
                    </p>
                    <div className="space-y-2">
                      {users.map(user => (
                        <label key={user.id} className="flex items-center p-2 hover:bg-gray-50 rounded">
                          <input
                            type="checkbox"
                            checked={formData.selectedUsers.includes(user.id)}
                            onChange={() => handleUserToggle(user.id)}
                            className="mr-3"
                          />
                          <div>
                            <p className="font-medium text-sm">
                              {user.firstName} {user.lastName}
                            </p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading || !formData.title.trim() || !formData.message.trim()}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {loading ? 'Enviando...' : 'Enviar Notificación'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Statistics Tab */}
      {activeTab === 'stats' && (
        <div className="space-y-6">
          {stats && (
            <>
              {/* Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="flex items-center">
                    <Bell className="w-8 h-8 text-blue-600" />
                    <div className="ml-4">
                      <p className="text-sm text-gray-600">Total Enviadas</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.totalCount}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="flex items-center">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                    <div className="ml-4">
                      <p className="text-sm text-gray-600">Leídas</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.readCount}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="flex items-center">
                    <MessageSquare className="w-8 h-8 text-orange-600" />
                    <div className="ml-4">
                      <p className="text-sm text-gray-600">Sin Leer</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.unreadCount}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-sm">%</span>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm text-gray-600">Tasa de Lectura</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.readRate}%</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* By Type */}
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Notificaciones por Tipo (Últimos 30 días)
                </h3>
                <div className="space-y-3">
                  {stats.byType.map(({ type, count }) => (
                    <div key={type} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                      <div className="flex items-center gap-3">
                        {getTypeIcon(type)}
                        <span className="font-medium capitalize">{type}</span>
                      </div>
                      <span className="font-bold text-gray-900">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;